import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import http from './services/http';

const DEFAULT_LOCALE = 'en';
export const LOCALE_STORAGE_KEY = 'locale';

const MODULE_NAMESPACES = ['admin', 'report', 'apex-forge'];
const CACHE_KEY_PREFIX = 'i18n_cache_';

type CachedResources = Record<string, Record<string, unknown>>;

function readCache(locale: string): CachedResources | null {
    try {
        const raw = localStorage.getItem(CACHE_KEY_PREFIX + locale);
        return raw ? (JSON.parse(raw) as CachedResources) : null;
    } catch {
        return null;
    }
}

function writeCache(locale: string, resources: CachedResources): void {
    try {
        localStorage.setItem(CACHE_KEY_PREFIX + locale, JSON.stringify(resources));
    } catch {
        // storage unavailable/full — safe to ignore, just skip caching
    }
}

const initialLocale = localStorage.getItem(LOCALE_STORAGE_KEY) ?? DEFAULT_LOCALE;
const cached = readCache(initialLocale);

export const hasCachedTranslations = cached !== null;

void i18next.use(initReactI18next).init({
    lng: initialLocale,
    fallbackLng: DEFAULT_LOCALE,
    ns: ['translation', ...MODULE_NAMESPACES],
    defaultNS: 'translation',
    resources: cached ? { [initialLocale]: cached } : {},
    interpolation: { escapeValue: false },
    parseMissingKeyHandler: () => '',
});

export async function loadTranslations(locale: string = localStorage.getItem(LOCALE_STORAGE_KEY) ?? DEFAULT_LOCALE): Promise<void> {
    try {
        const resources: CachedResources = {};

        const { data } = await http.get(`/api/translations/${locale}`);
        resources.translation = data.strings;
        i18next.addResourceBundle(locale, 'translation', data.strings, true, true);

        await Promise.all(
            MODULE_NAMESPACES.map(async (namespace) => {
                const { data: moduleData } = await http.get(`/api/translations/${locale}/${namespace}`);
                resources[namespace] = moduleData.strings;
                i18next.addResourceBundle(locale, namespace, moduleData.strings, true, true);
            }),
        );

        await i18next.changeLanguage(locale);
        localStorage.setItem(LOCALE_STORAGE_KEY, locale);
        writeCache(locale, resources);
    } catch (error) {
        console.error('Failed to load translations', error);
    }
}

export async function setLocale(locale: string): Promise<void> {
    await loadTranslations(locale);
    try {
        await http.post('/api/locale', { locale });
    } catch (error) {
        console.error('Failed to persist locale', error);
    }
}

export default i18next;
